// ============================================================
// DOCTORS PAGES
// All Doctors + Onboarding (doctors who haven't been activated yet)
// ============================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doctorAPI } from '../services/api';
import {
  PageHeader, FilterBar, DataTable, AvatarCell,
  ActionBtn, Modal, FormField, Input, Select
} from '../components/Shared';
import toast from 'react-hot-toast';
import styles from './Doctors.module.css';

const AVATAR_COLORS = ['#C0185A','#1E3A5F','#0D9A6A','#E07B2A','#6B46C1','#2B6CB0'];
function avatarColor(i) { return AVATAR_COLORS[i % AVATAR_COLORS.length]; }

// Greek labels for the specialty_category enum stored in the DB
const SPECIALTY_CATEGORIES = [
  { value: 'general_practitioner', label: 'Γενικός Ιατρός' },
  { value: 'specialist',           label: 'Ειδικός' },
  { value: 'mental_health',        label: 'Ψυχική Υγεία' },
  { value: 'allied_health',        label: 'Επαγγελματίες Υγείας' },
];

// @add-doctor-specialty-to-category-map
// Maps the specialty dropdown value to the DB enum category, so admins can't
// accidentally create a mismatch (e.g., specialty="Παθολόγος" +
// category="general_practitioner"). Auto-applied in the specialty Select's onChange.
const SPECIALTY_TO_CATEGORY = {
  'Γενικός / Οικογενειακός Ιατρός': 'general_practitioner',
  'Παθολόγος': 'specialist',
  'Ιατρός': 'general_practitioner',
};

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  specialtyCategory: 'general_practitioner',
  specialty: 'Γενικός / Οικογενειακός Ιατρός',
  bio: '',
  languages: 'el',
};

// ── ALL DOCTORS ──
export function AllDoctorsPage() {
  const [doctors, setDoctors] = useState([]);
  const [filter,  setFilter]  = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await doctorAPI.getAll(filter !== 'all' ? { specialty: filter } : {});
      setDoctors(data.data);
    } catch { toast.error('Αδυναμία φόρτωσης ιατρών'); }
    finally { setLoading(false); }
  };

  // @m24-deps: dependency list left exactly as it was when linting was switched on 25 Sep 2026.
  // Adding the missing dependency can change how often this effect runs, so it is a behaviour
  // change to make deliberately and test, not a lint tidy-up. Recorded, not silenced by turning
  // the rule off - it still guards every effect written from here on.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        // Optional fields — sent only if filled, so the backend keeps NULLs
        // for columns the admin hasn't touched yet
        ...(form.phone.trim() && { phone: form.phone.trim() }),
        ...(form.specialty.trim() && { specialty: form.specialty.trim() }),
        specialtyCategory: form.specialtyCategory,
        ...(form.bio.trim() && { bio: form.bio.trim() }),
        languages: form.languages.split(',').map(s => s.trim()).filter(Boolean),
      };
      const { data } = await doctorAPI.create(payload);
      toast.success('✅ Ιατρός δημιουργήθηκε. Συμπληρώστε τα υπόλοιπα στοιχεία και πατήστε "Ενεργοποίηση".');
      setShowAdd(false);
      setForm(EMPTY_FORM);
      // Navigate straight to the new doctor's profile so admin can fill the rest
      if (data?.data?.doctorId) {
        navigate(`/doctors/${data.data.doctorId}`);
      } else {
        load();
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Σφάλμα δημιουργίας';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const COLS = [
    { key:'name', label:'Ιατρός', width:'2fr', render: (r,i) => <AvatarCell initials={`${r.firstName?.[0] || '?'}${r.lastName?.[0] || ''}`} name={`Δρ. ${r.firstName || ''} ${r.lastName || ''}`} sub={r.email} color={avatarColor(i)} /> },
    { key:'specialty', label:'Ειδικότητα', width:'1.5fr', render: r => r.specialty || '—' },
    { key:'languages', label:'Γλώσσες', width:'0.8fr', render: r => r.languages?.join(', ').toUpperCase() || '—' },
    { key:'count', label:'Διαβουλεύσεις', width:'1fr', render: r => <strong>{r.totalConsultations || 0}</strong> },
    { key:'state', label:'Κατάσταση', width:'1fr', render: r => {
      // Three states: onboarding (not approved), leaver, active
      if (!r.isApproved) {
        return <span className="pill" style={{ background: '#FEF3C7', color: '#92400E' }}>📋 Onboarding</span>;
      }
      if (r.status === 'leaver' || r.is_leaver) {
        return <span className="pill" style={{ background: '#FEE2E2', color: '#991B1B' }}>Αποχωρήσας</span>;
      }
      return <span className="pill pill-green">● Ενεργός</span>;
    }},
    { key:'actions', label:'', width:'120px', render: r => (
      <div style={{display:'flex',gap:6}}>
        <ActionBtn variant="default" onClick={() => navigate(`/doctors/${r.id}`)}>Προβολή</ActionBtn>
      </div>
    )},
  ];

  const FILTERS = [
    { value:'all', label:`Όλοι (${doctors.length})` },
    { value:'General Practitioner', label:'GP' },
    { value:'Specialist', label:'Ειδικοί' },
    { value:'Mental Health', label:'Ψυχική Υγεία' },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Όλοι οι Ιατροί"
        subtitle={`${doctors.length} ιατροί στην πλατφόρμα`}
        action={<ActionBtn variant="primary" onClick={() => setShowAdd(true)}>+ Προσθήκη Ιατρού</ActionBtn>}
      />
      <FilterBar filters={FILTERS} active={filter} onChange={setFilter} />
      {loading ? <div className={styles.loading}>Φόρτωση...</div> : (
        <DataTable columns={COLS} rows={doctors} />
      )}

      {/* Add doctor modal — kicks off the onboarding flow */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Προσθήκη Νέου Ιατρού" size="md">
        <div style={{ padding: '0 4px 4px', fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
          Δημιουργείτε τον λογαριασμό. Στη συνέχεια θα συμπληρώσετε τα υπόλοιπα στοιχεία και έγγραφα στο προφίλ του ιατρού πριν την ενεργοποίηση.
        </div>
        <div className={styles.formGrid}>
          <FormField label="Όνομα" required>
            <Input placeholder="π.χ. Νικόλας" value={form.firstName} onChange={e => setForm({...form, firstName:e.target.value})} />
          </FormField>
          <FormField label="Επώνυμο" required>
            <Input placeholder="π.χ. Σαΐμον" value={form.lastName} onChange={e => setForm({...form, lastName:e.target.value})} />
          </FormField>
          <FormField label="Email" required>
            <Input type="email" placeholder="doctor@docnow.gr" value={form.email} onChange={e => setForm({...form, email:e.target.value})} />
          </FormField>
          <FormField label="Τηλέφωνο">
            <Input placeholder="+306900000000" value={form.phone} onChange={e => setForm({...form, phone:e.target.value})} />
          </FormField>
          <FormField label="Κατηγορία Ειδικότητας" required>
            <Select value={form.specialtyCategory} onChange={e => setForm({...form, specialtyCategory:e.target.value})}>
              {SPECIALTY_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </Select>
          </FormField>
          {/* @add-doctor-specialty-dropdown */}
          <FormField label="Ειδικότητα">
            <Select value={form.specialty} onChange={e => {
              const specialty = e.target.value;
              setForm({ ...form, specialty, specialtyCategory: SPECIALTY_TO_CATEGORY[specialty] || form.specialtyCategory });
            }}>
              <option value="Γενικός / Οικογενειακός Ιατρός">Γενικός / Οικογενειακός Ιατρός</option>
              <option value="Παθολόγος">Παθολόγος</option>
              <option value="Ιατρός">Ιατρός</option>
            </Select>
          </FormField>
          {/* @add-doctor-languages-picker */}
          <div style={{gridColumn:'1/-1'}}>
            <FormField label="Γλώσσες">
              {(() => {
                const LANGS = [['el','🇬🇷'],['en','🇬🇧'],['fr','🇫🇷'],['es','🇪🇸'],['de','🇩🇪'],['it','🇮🇹'],['pt','🇵🇹'],['ar','🇸🇦'],['ru','🇷🇺'],['tr','🇹🇷']];
                const selected = form.languages.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
                const toggle = (code) => {
                  const set = new Set(selected);
                  if (set.has(code)) set.delete(code); else set.add(code);
                  const ordered = LANGS.filter(([c]) => set.has(c)).map(([c]) => c);
                  setForm({ ...form, languages: ordered.join(', ') });
                };
                return (
                  <details style={{ width: '100%' }}>
                    <summary style={{ listStyle: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: 10, borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer', background: '#fff', boxSizing: 'border-box' }}>
                      {selected.length === 0
                        ? <span style={{ color: 'var(--muted)' }}>Επιλέξτε γλώσσες…</span>
                        : <span>{LANGS.filter(([c]) => selected.includes(c)).map(([c, f]) => f + ' ' + c.toUpperCase()).join('   ')}</span>}
                      <span style={{ marginLeft: 'auto', color: 'var(--muted)' }}>▾</span>
                    </summary>
                    <div style={{ marginTop: 6, border: '1px solid var(--border)', borderRadius: 8, padding: 6, maxHeight: 240, overflowY: 'auto' }}>
                      {LANGS.map(([code, flag]) => {
                        const on = selected.includes(code);
                        return (
                          <div key={code} onClick={() => toggle(code)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', background: on ? 'var(--bg)' : 'transparent' }}>
                            <input type="checkbox" checked={on} readOnly style={{ pointerEvents: 'none' }} />
                            <span style={{ fontSize: 16 }}>{flag}</span>
                            <span style={{ fontWeight: 600 }}>{code.toUpperCase()}</span>
                          </div>
                        );
                      })}
                    </div>
                  </details>
                );
              })()}
            </FormField>
          </div>
          <div style={{gridColumn:'1/-1'}}>
            <FormField label="Βιογραφικό">
              <textarea className={styles.textarea} rows={3} placeholder="Σύντομο βιογραφικό..." value={form.bio} onChange={e => setForm({...form, bio:e.target.value})} />
            </FormField>
          </div>
        </div>
        <div className={styles.modalFooter}>
          <ActionBtn variant="default" onClick={() => setShowAdd(false)} disabled={submitting}>Ακύρωση</ActionBtn>
          <ActionBtn
            variant="primary"
            onClick={handleCreate}
            disabled={!form.firstName || !form.lastName || !form.email || submitting}
          >
            {submitting ? 'Δημιουργία...' : 'Δημιουργία Λογαριασμού'}
          </ActionBtn>
        </div>
      </Modal>
    </div>
  );
}

// ── ONBOARDING DOCTORS ──
// Doctors where is_approved = false — they're either freshly created and the
// admin needs to fill in the rest, or admin tried to activate but a validation
// gate failed and they need to fix something. From this list, click "Άνοιγμα
// Προφίλ" to go to the profile and complete the onboarding.
export function PendingDoctorsPage() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await doctorAPI.getPending();
      setPending(data.data);
    } catch { toast.error('Αδυναμία φόρτωσης'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const COLS = [
    { key:'name', label:'Ιατρός', width:'2fr', render: (r,i) => <AvatarCell initials={`${r.firstName?.[0] || '?'}${r.lastName?.[0] || ''}`} name={`Δρ. ${r.firstName || ''} ${r.lastName || ''}`} sub={r.email} color={avatarColor(i)} /> },
    { key:'specialty', label:'Ειδικότητα', width:'1.5fr', render: r => r.specialty || '—' },
    { key:'created', label:'Δημιουργία', width:'1fr', render: r => r.createdAt ? new Date(r.createdAt).toLocaleDateString('el-GR') : '—' },
    { key:'langs', label:'Γλώσσες', width:'0.8fr', render: r => r.languages?.join(', ').toUpperCase() || '—' },
    { key:'actions', label:'', width:'160px', render: r => (
      <div style={{display:'flex',gap:6}}>
        <ActionBtn variant="primary" onClick={() => navigate(`/doctors/${r.id}`)}>Άνοιγμα Προφίλ →</ActionBtn>
      </div>
    )},
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Ιατροί σε Onboarding"
        subtitle={`${pending.length} ιατροί χωρίς ενεργοποίηση`}
      />
      <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: 14, marginBottom: 20, fontSize: 13, color: '#92400E' }}>
        💡 Οι ιατροί παρακάτω έχουν δημιουργηθεί αλλά δεν είναι ενεργοί ακόμα. Ανοίξτε το προφίλ για να συμπληρώσετε στοιχεία και έγγραφα, και πατήστε "Ενεργοποίηση" όταν είναι όλα έτοιμα.
      </div>
      {loading ? <div className={styles.loading}>Φόρτωση...</div> : (
        pending.length === 0 ? (
          <div className={styles.allGood}>
            <div style={{fontSize:48,marginBottom:14}}>✅</div>
            <div style={{fontSize:16,fontWeight:700,color:'var(--navy)'}}>Δεν υπάρχουν ιατροί σε onboarding</div>
            <div style={{fontSize:13,color:'var(--muted)',marginTop:6}}>Όλοι οι ιατροί είναι ενεργοί</div>
          </div>
        ) : (
          <DataTable columns={COLS} rows={pending} />
        )
      )}
    </div>
  );
}